import { useState, useEffect } from "react";

import { Alert, Button, Spinner } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";

import { useMutation, useQuery } from "@tanstack/react-query";
import { SelectButton } from "primereact/selectbutton";

import {
  AppointmentStatus,
  AppointmentStatusSlug,
} from "@/_constants/appointment";
import { ServiceOrderType } from "@/_constants/serviceOrder";
import { CheckBox } from "@/common/components/form/CheckBox";
import SelectForm from "@/common/components/form/SelectForm";
import Loader from "@/common/components/Loader";
import {
  listOperator,
  listOperatorAvailability,
} from "@/modules/serviceOrdering/services";
import { useServiceInventoryStore } from "@/modules/serviceOrdering/store/ServiceInventoryStore";

interface IServiceOrderingOperatorFormProps {
  specificLabel: string;
  type: ServiceOrderType;
  onExpertByDefault: boolean;
}

const ServiceOrderingOperatorForm: React.FC<
  IServiceOrderingOperatorFormProps
> = ({ specificLabel, type, onExpertByDefault }) => {
  const user = useSelector<any, any>((state) => state?.authReducer?.user);
  const { control, watch, resetField, setValue } = useFormContext();
  const { getStartDate } = useServiceInventoryStore((state) => state);

  // Determine if the service type is a diagnostic type
  const isDiagnosticType = (orderType: ServiceOrderType): boolean => {
    return [
      ServiceOrderType.DIAGNOSTIC,
      ServiceOrderType.PPPT,
      ServiceOrderType.CONDOMINIUM_VISIT,
      ServiceOrderType.CONDOMINIUM_CREATION,
      ServiceOrderType.ENERGY_AUDIT,
    ].includes(orderType);
  };

  // Set serviceType based on the type prop
  const serviceType = isDiagnosticType(type) ? "diagnostic" : "edl";

  useEffect(() => {
    if (onExpertByDefault) {
      setValue("isManagedByMainGroup", true);
    }
  }, [onExpertByDefault, setValue]);

  const isDwellSwitch = watch("isManagedByMainGroup") || false;

  const idDwelling = watch("dwelling") ? watch("dwelling").id : null;

  const managersQuery = useQuery<any, Error>(
    ["managers", idDwelling, serviceType],
    () => {
      resetField("operator");
      resetField("manager");
      return listOperator(watch("dwelling").id, serviceType);
    },
    {
      retry: 1,
      enabled: !!idDwelling,
    }
  );

  const {
    data: operators,
    isLoading,
    error,
  } = useQuery<any, Error>(
    ["operators", idDwelling, isDwellSwitch, serviceType],
    () => {
      resetField("operator");
      resetField("manager");
      return listOperator(watch("dwelling").id, serviceType, isDwellSwitch);
    },
    {
      retry: 1,
      enabled: !!idDwelling,
    }
  );

  const operatorAvailability = useMutation(
    () => {
      resetField("operator");
      return listOperatorAvailability({
        dwellingId: idDwelling,
        isManagedByMainGroup: isDwellSwitch,
        startDate: getStartDate(
          type,
          watch("inventory")?.appointment?.startDate,
          watch("tenantLeaseEndDate"),
          watch("tenantLeaseStartDate")
        ),
        type: type,
      });
    },
    {
      retry: 1,
    }
  );

  return (
    <div className="row">
      {!onExpertByDefault ? (
        <div className="col-sm">
          <FieldSwitch
            name="isManagedByMainGroup"
            onChange={() => {
              setValue("operator", null);
              operatorAvailability.reset();
            }}
            control={control}
            specificLabel={specificLabel}
          />
        </div>
      ) : null}

      <div className={onExpertByDefault ? "col-sm-6" : "col-sm"}>
        <FieldManager
          name="manager"
          isLoading={managersQuery.isLoading}
          error={managersQuery.error}
          operators={managersQuery.data}
          control={control}
          watch={watch}
        />
      </div>
      {isDwellSwitch || user.isMainGroupMember ? (
        <div className={onExpertByDefault ? "col-sm-6" : "col-sm"}>
          <FieldOperator
            isMainGroupMember={user.isMainGroupMember}
            idDwelling={idDwelling}
            operatorAvailability={operatorAvailability}
            name="operator"
            isLoading={isLoading}
            error={error}
            operators={
              operatorAvailability?.data
                ? operatorAvailability?.data
                : operators
            }
            isGroup={!!operatorAvailability?.data}
            control={control}
            watch={watch}
          />
        </div>
      ) : null}
    </div>
  );
};

const FieldSwitch = ({
  name,
  control,
  onChange,
  specificLabel,
}: {
  name: string;
  control: any;
  onChange: () => void;
  specificLabel: string;
}) => {
  const options = ["Expert", "Mon agence"];

  return (
    <div className="d-flex flex-column gap-3 col-md w-100 justify-content-start h-100">
      <span className="serviceOrdering-subtitle">Intervenant</span>
      <div>
        <div className="serviceOrdering-text mb-3">
          Je veux que mon {specificLabel} soit réalisé par :
        </div>
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <SelectButton
              style={{ display: "flex" }}
              value={field.value ? options[0] : options[1]}
              onChange={(e) => {
                field.onChange(e.value === options[0]);
                onChange();
              }}
              options={options}
            />
          )}
        />
      </div>
      <div />
    </div>
  );
};

const FieldOperator = ({
  name,
  isLoading,
  operators,
  isGroup = false,
  control,
  error,
  watch,
  idDwelling,
  operatorAvailability,
  isMainGroupMember,
}: {
  name: string;
  isLoading: boolean;
  operators: any;
  isGroup: boolean;
  error: any;
  control: any;
  watch: any;
  idDwelling: string;
  operatorAvailability: any;
  isMainGroupMember: boolean;
}) => {
  const [isOperatorAvailabilityVisible, setIsOperatorAvailabilityVisible] =
    useState(false);

  const handleOperatorAvailabilityClick = () => {
    if (isOperatorAvailabilityVisible) {
      operatorAvailability.reset();
    } else {
      operatorAvailability.mutate();
    }
    setIsOperatorAvailabilityVisible(!isOperatorAvailabilityVisible);
  };

  return (
    <div className="d-flex flex-column gap-3 col-md w-100">
      <span className="serviceOrdering-subtitle">Opérateur</span>

      {!watch("dwelling")?.id ? (
        <span className="serviceOrdering-text serviceOrdering-text-error">
          Veuillez renseigner un bien
        </span>
      ) : isLoading ? (
        <Loader />
      ) : !error ? (
        <>
          <div className="serviceOrdering-text">
            Je veux attribuer la prestation à :
          </div>
          <div className="d-flex flex-column gap-1 w-100">
            <div className={"d-flex align-items-end w-100"}>
              <SelectForm
                isClearable={true}
                extraSelect={
                  <>
                    {idDwelling ? (
                      <Button
                        variant={
                          isOperatorAvailabilityVisible
                            ? "primary"
                            : "outline-secondary"
                        }
                        className="align-items-end me-1"
                        onClick={handleOperatorAvailabilityClick}
                      >
                        {operatorAvailability.isLoading ? (
                          <Spinner
                            as="span"
                            animation="grow"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <i className="pi pi-calendar" />
                        )}
                      </Button>
                    ) : null}
                  </>
                }
                defaultValue={watch(name)}
                label="Opérateur"
                name={name}
                options={operators}
                control={control}
                isGroup={isGroup}
              />
            </div>
          </div>
          {watch(name) ? (
            <div>
              {isMainGroupMember ? (
                <SelectForm
                  defaultValue={{
                    value: AppointmentStatusSlug.PLANNING,
                    label: AppointmentStatus.PLANNING,
                  }}
                  label="Status"
                  name={"state"}
                  options={Object.keys(AppointmentStatusSlug).map((slug) => {
                    return {
                      value: slug,
                      label: AppointmentStatus[slug],
                    };
                  })}
                  control={control}
                  isGroup={isGroup}
                />
              ) : (
                <CheckBox
                  control={control}
                  label='Passer la commande en statut "confirmé"'
                  name={"isConfirmed"}
                />
              )}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const FieldManager = ({
  name,
  isLoading,
  error,
  operators,
  control,
  watch,
}: {
  name: string;
  isLoading: boolean;
  error: any;
  operators: any;
  control: any;
  watch: any;
}) => (
  <div className="d-flex flex-column gap-3 col-md">
    <span className="serviceOrdering-subtitle">Gestionnaire / commercial</span>

    {!watch("dwelling")?.id ? (
      <span className="serviceOrdering-text serviceOrdering-text-error">
        Veuillez renseigner un bien
      </span>
    ) : isLoading ? (
      <Loader />
    ) : !error && operators.length > 0 ? (
      <>
        <div className="serviceOrdering-text">
          Je veux attribuer la commande à :
        </div>
        <div className="d-flex flex-column gap-1">
          <SelectForm
            defaultValue={watch(name)}
            isClearable={true}
            label="Gestionnaire / commercial"
            name={name}
            options={operators}
            control={control}
          />
        </div>
      </>
    ) : error ? (
      <Alert variant="danger">
        <small>Erreur chargement des données : {error}</small>
      </Alert>
    ) : (
      <Alert variant="warning">
        <small>Aucun gestionnaire / commercial disponible pour ce bien</small>
      </Alert>
    )}
  </div>
);

export default ServiceOrderingOperatorForm;
